<template>
    <div class="apprentice-simple-card">
        <design-2021-apprentice-data-table
            class="slu-cohort-modules"
            :items="tableData"
            :fields="moduleFields"
            :show-empty="true">
            <template v-slot:options>
                <b-button
                    class="btn-add btn-add-slu-cohort"
                    v-b-modal="'add-slu-module-modal'"
                    v-if="$authUser.hasRoleOrAdmin('slu')"
                    type="button"
                    label="create-new-slu-module"
                    variant="primary">
                    {{ $t('slu_dashboard.add_module') }}
                </b-button>
                <b-button
                    class="btn-add btn-copy-to-slu-cohort"
                    v-b-modal="'copy-slu-module'"
                    v-if="$authUser.hasRoleOrAdmin('slu')"
                    type="button"
                    label="copy-slumodule-from-other-cohort"
                    variant="primary">
                    {{ $t('slu_dashboard.copy_module') }}
                </b-button>
                <b-form-select
                    class="apprentice-primary-select las-type-filter"
                    v-model="selectedLasType"
                    :options="LasTypeOptions">
                </b-form-select>
            </template>

            <template v-slot:cell(name)="data">
                {{ data.item.name }} <span v-if="!data.item.required">*</span>
            </template>

            <template v-slot:cell(required)="data">
                <div :class="`required-status ${data.item.required ? 'text-success' : 'text-primary'}`">
                    <i :class="`far fa-fw ${data.item.required ? 'fa-check-circle' : 'fa-times-circle'}`"></i>
                </div>
            </template>

            <template v-slot:cell(progress)="data">
                <b-progress :max="getCalculatedMaxedHours(data.item)">
                    <b-progress-bar
                        v-b-tooltip.hover
                        :value="getCalculatedFinishedHours(data.item)"
                        :label="getCalculatedLabelValue(data.item)"
                        variant="success"
                        :title="$t('slu_dashboard.completed_hours')">
                    </b-progress-bar>
                </b-progress>
            </template>

            <template v-slot:cell(action)="data">
                <div class="management-actions">
                    <b-button
                        class="btn-open btn-open-slu-module btn-"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :title="$t('common.open')"
                        @click="openSluModule(data.item.id)">
                    </b-button>
                    <b-button
                        class="btn-edit btn-edit-slu-module btn-"
                        v-b-tooltip.hover
                        v-if="$authUser.hasRoleOrAdmin('slu')"
                        variant="primary-icon-only"
                        :title="$t('common.edit')"
                        @click="editSluModule(data.item.id)">
                    </b-button>
                    <b-button
                        class="btn-remove btn-remove-slu-module"
                        v-if="$authUser.hasRoleOrAdmin('slu')"
                        variant="danger-icon-only"
                        @click="deleteModule(data.item.id)">
                    </b-button>
                </div>
            </template>

            <template #empty>
                <div class="not-found with-header attachments-list">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">{{ $t('slu_dashboard.no_modules_found') }}</div>
                </div>
            </template>
        </design-2021-apprentice-data-table>
        <design-2021-add-slu-module-modal-component
            :slu-school-settings-in="sluSchoolSettingsIn"
            :slu-cohort-in="sluCohortIn"
            :slu-labels-in="sluLabelsIn"
            @updateModules="addModule($event)"
            ref="add-slu-module-modal">
        </design-2021-add-slu-module-modal-component>
        <design-2021-update-slu-module-modal-component
            :slu-cohort-in="sluCohortIn"
            :slu-labels-in="sluLabelsIn"
            :slu-school-settings-in="sluSchoolSettingsIn"
            @updateModule="updateModule($event)"
            ref="edit-slu-module-modal">
        </design-2021-update-slu-module-modal-component>
        <design-2021-copy-slu-module-modal-component
            :slu-cohort-in="sluCohortIn"
            @updateModules="addModule($event)"
            ref="copy-slu-module">
        </design-2021-copy-slu-module-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-slu-cohort-modules-index-tab-component',
    props: ['sluCourseIn', 'sluCohortIn', 'sluLabelsIn', 'sluSchoolSettingsIn'],
    data() {
        return {
            sluCourse: this.sluCourseIn,
            sluCohort: this.sluCohortIn,
            sluModules: this.sluCohortIn.modules,
            max: 100,
            value: 67,
            selectedLasType: null,
        };
    },
    computed: {
        tableData() {
            let users = null;
            if (this.selectedLasType) {
                let lasGroup0 = this.sluCohort.las_groups.find((group) => group.name === this.selectedLasType);
                let lasGroup1 = this.sluCohort.las_main_groups.find((group) => group.name === this.selectedLasType);
                let lasGroup2 = lasGroup0 ? lasGroup0 : lasGroup1;
                let userIdArray = lasGroup2.students.map((student) => student.user_id);
                users = this.sluCohortIn.users.filter((user) => userIdArray.includes(user.id));
            } else {
                users = this.sluCohortIn.users;
            }

            return this.sluModules.map((module) => {
                let totalStudents = users.length;

                let finishedStudents = users.filter((user) => {
                    if (user.slu_module_records) {
                        const sluRecord = user.slu_module_records.find((record) => record.slu_module_id === module.id);
                        if (sluRecord) {
                            return sluRecord.completed;
                        } else {
                            return false;
                        }
                    }
                }).length;

                return {
                    id: module.id,
                    file: module.file,
                    hours: module.hours,
                    name: module.name,
                    required: module.required,
                    stc_code: module.stc_code,
                    totalStudents: totalStudents,
                    finishedStudents: finishedStudents,
                };
            });
        },
        moduleFields() {
            let fields = [
                {
                    key: 'name',
                    label: this.$t('common.name'),
                },
                {
                    key: 'hours',
                    label: this.$t('slu_dashboard.slu_hours'),
                },
                {
                    key: 'required',
                    label: this.$t('slu_dashboard.required'),
                },
            ];
            if (this.sluCohortIn.stc_course_uuid && this.sluSchoolSettingsIn.stc_sync) {
                fields.push({
                    key: 'stc_code',
                    label: 'SomtodayConnect-code',
                });
            }
            fields = fields.concat([
                {
                    key: 'progress',
                    label: this.$t('slu_dashboard.finished'),
                },
                {
                    key: 'action',
                    label: this.$t('common.actions'),
                },
            ]);
            return fields;
        },
        LasTypeOptions() {
            let lasGroups = [...this.sluCohortIn.las_groups, ...this.sluCohortIn.las_main_groups];
            let returnGroups = lasGroups.map((group) => {
                return {
                    value: group.name,
                    text: group.name,
                };
            });
            returnGroups.unshift({ value: null, text: this.$t('slu_dashboard.everything') });

            return returnGroups;
        },
    },
    methods: {
        openSluModule(moduleId) {
            this.alert.loading();
            window.location.href = this.routes.sluModuleGet(this.sluCourse.id, this.sluCohort.id, moduleId);
        },

        updateModule(data) {
            this.sluModules = this.sluModules.map((module) => {
                if (module.id === data.id) {
                    const newModule = { ...module, ...data };
                    return newModule;
                } else {
                    return module;
                }
            });
        },
        editSluModule(moduleId) {
            const module = this.sluModules.find((module) => module.id === moduleId);
            this.$refs['edit-slu-module-modal'].show(module);
        },
        addModule(module) {
            this.sluModules.push(module);
        },
        deleteModule(id) {
            this.alert
                .confirm(
                    this.$t('slu_dashboard.delete_module'),
                    this.$t('slu_dashboard.are_you_sure_you_want_to_delete_this_module'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.alert.saving();
                    this.axios.delete(this.routes.sluModuleDelete(this.sluCourse.id, this.sluCohort.id, id)).then(
                        (response) => {
                            this.sluModules = this.sluModules.filter((item) => item.id !== id);
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },

        getCalculatedMaxedHours(module) {
            return module.totalStudents * module.hours;
        },

        getCalculatedFinishedHours(module) {
            return module.finishedStudents * module.hours;
        },

        getCalculatedLabelValue(module) {
            return `${(((module.finishedStudents * module.hours) / (module.totalStudents * module.hours)) * 100).toFixed(0)}%`;
        },
    },
};
</script>

<style scoped lang="scss">
.required-status {
    display: inline-block;
    font-size: 1.3125rem;
    line-height: 1.5;
    width: auto;
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    select {
        margin-bottom: 0;
    }

    .slu-cohort-modules ::v-deep {
        .apprentice-table-header {
            .options {
                column-gap: map-get($grid-gutter-spacing, 'fifteen');
                row-gap: map-get($grid-gutter-spacing, 'fifteen');
                flex-wrap: wrap;
            }

            .las-type-filter {
                width: auto;
                max-width: 250px;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .slu-cohort-modules ::v-deep {
        .apprentice-table-header {
            flex-wrap: wrap;
            flex-direction: row;
        }

        .apprentice-table-filters,
        .options {
            flex: 0 1 auto;
        }

        .options {
            margin-left: auto;
        }
    }
}
</style>
